import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { DepositProduct, Product } from '../types/Product';
import { DepositApi } from '../api/DepositApi';
import { PersonalCabinetApi, PersonalDeposit } from '../api/PersonalCabinetApi';

interface Company {
  id: number;
  name: string;
}

export default function InventoryPage() {
  const { t } = useTranslation();
  const [deposits, setDeposits] = useState<PersonalDeposit[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedDeposit, setSelectedDeposit] = useState<number | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [companyProducts, setCompanyProducts] = useState<Product[]>([]);
  const [depositProducts, setDepositProducts] = useState<DepositProduct[]>([]); // left table
  const [movedProducts, setMovedProducts] = useState<{ product: Product, count: number }[]>([]); // right table
  const [scanValue, setScanValue] = useState('');
  const [manualProductId, setManualProductId] = useState<number | null>(null);
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bulkScanMode, setBulkScanMode] = useState(false);
  const [bulkCount, setBulkCount] = useState(0);
  const scanInputRef = useRef<HTMLInputElement>(null);

  const matchesScannedCode = (product: Product, scannedValue: string) => {
    const code = scannedValue.trim();
    if (!code) return false;

    const candidates = [
      product.customBarcode,
      product.customQrCode,
      product.barcode,
      product.qrCode,
    ].filter((value): value is string => Boolean(value && value.trim()));

    return candidates.some(candidate => candidate.trim() === code);
  };

  const getProductDisplayName = (product: Product) => {
    const details = [product.name, product.firm, product.model, product.size]
      .map(value => value?.trim())
      .filter((value): value is string => Boolean(value));
    return details.join(' · ') || t('inventory.unknownProduct');
  };

  // Fetch deposits and companies on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [depositsRes, companiesRes] = await Promise.all([
          PersonalCabinetApi.getPersonalDeposits(),
          PersonalCabinetApi.getPersonalCompanies()
        ]);
        setDeposits(depositsRes.data);
        setCompanies(companiesRes.data);
        
        // Set first company as default
        if (companiesRes.data.length > 0) {
          setSelectedCompany(companiesRes.data[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch initial data:', err);
      }
    };
    
    fetchData();
    
  }, []);

  // Fetch products for selected company and clear deposit selection
  useEffect(() => {
    if (selectedCompany) {
      fetchCompanyProducts(selectedCompany);
    }
  }, [selectedCompany]);

  // Keep selected deposit aligned with selected company and default to the first available deposit.
  useEffect(() => {
    if (!selectedCompany) {
      setSelectedDeposit(null);
      return;
    }

    const companyDeposits = deposits.filter(
      deposit => deposit.company && deposit.company.id === selectedCompany
    );

    if (companyDeposits.length === 0) {
      setSelectedDeposit(null);
      return;
    }

    const isCurrentDepositValid = companyDeposits.some(
      deposit => deposit.id === selectedDeposit
    );

    if (!isCurrentDepositValid) {
      setSelectedDeposit(companyDeposits[0].id);
    }
  }, [selectedCompany, deposits, selectedDeposit]);

  // Prepare both scanner and manual entry when bulk counting starts. Focus is
  // returned after each scan instead of being forcibly stolen from manual controls.
  useEffect(() => {
    if (bulkScanMode && scanInputRef.current) {
      scanInputRef.current.focus();
    }
    setManualEntryOpen(bulkScanMode);
    setManualProductId(null);
    setScanValue('');
  }, [bulkScanMode]);

  // Fetch products for selected deposit
  // useEffect(() => {
  //   if (selectedDeposit != null) {
  //     api.get(`/deposit-products/by-deposit/${selectedDeposit}`).then(res => setInventory(res.data));
  //     setScanned([]);
  //   }
  // }, [selectedDeposit]);

  useEffect(() => {
    if (selectedDeposit) {
      console.log(`[InventoryPage] Deposit selection changed to: ${selectedDeposit}`);
      // Clear previous deposit's data when switching deposits
      setDepositProducts([]);
      setMovedProducts([]);
      setManualProductId(null);
      fetchDepositProducts(selectedDeposit);
    } else {
      console.log('[InventoryPage] No deposit selected, clearing data');
      // Clear data when no deposit is selected
      setDepositProducts([]);
      setMovedProducts([]);
      setManualProductId(null);
    }
  }, [selectedDeposit]);

  const fetchCompanyProducts = async (companyId: number) => {
    try {
      const { ProductApi } = await import('../api/ProductApi');
      // Include hidden/deleted products so scan add/remove still works in bulk flows
      const [visibleResponse, allResponse] = await Promise.all([
        ProductApi.getByCompany(companyId),
        ProductApi.getByCompany(companyId, true).catch(() => ({ data: [] }))
      ]);

      const visibleProducts = visibleResponse.data || [];
      const allProducts = allResponse.data || [];

      const productMap = new Map<number, Product>();
      visibleProducts.forEach((p: Product) => {
        if (p.id) productMap.set(p.id, p);
      });
      allProducts.forEach((p: Product) => {
        if (p.id && !productMap.has(p.id)) {
          productMap.set(p.id, p);
        }
      });

      setCompanyProducts(Array.from(productMap.values()));
    } catch (err) {
      console.error('Failed to fetch products for company:', err);
      setCompanyProducts([]);
    }
  };

  // Move product from left to right table (simulate scan or + button)
  const handleMoveRight = (dp: DepositProduct) => {
    // For both positive and negative quantities, just decrease the count
    // Keep ALL products in the left table regardless of quantity
    setDepositProducts(prev => {
      return prev.map(p =>
        p.product.id === dp.product.id ? { ...p, quantity: p.quantity - 1 } : p
      );
      // Removed the .filter(p => p.quantity > 0) that was removing products
    });
    
    // Add or update in right table
    setMovedProducts(prev => {
      const idx = prev.findIndex(mp => mp.product.id === dp.product.id);
      if (idx !== -1) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], count: copy[idx].count + 1 };
        return copy;
      } else {
        return [...prev, { product: dp.product, count: 1 }];
      }
    });
  };

  // Move product from right to left table (if not in left, add with -1 count)
  const handleMoveLeft = (mp: { product: Product, count: number }) => {
    setMovedProducts(prev => {
      const idx = prev.findIndex(p => p.product.id === mp.product.id);
      if (idx !== -1) {
        const copy = [...prev];
        if (copy[idx].count > 1) {
          copy[idx] = { ...copy[idx], count: copy[idx].count - 1 };
        } else {
          copy.splice(idx, 1);
        }
        return copy;
      }
      return prev;
    });
    setDepositProducts(prev => {
      const idx = prev.findIndex(p => p.product.id === mp.product.id);
      if (idx !== -1) {
        // Already in left, increase count
        return prev.map(p =>
          p.product.id === mp.product.id ? { ...p, quantity: p.quantity + 1 } : p
        );
      } else {
        // Not in left, add with 1 count
        return [...prev, { id: Math.random(), product: mp.product, quantity: 1, deposit: { id: selectedDeposit!, name: '' } }];
      }
    });
  };

  const handleManualCount = (product: Product | undefined) => {
    setError('');
    setSuccess('');

    if (!selectedDeposit || !selectedCompany || !product?.id) {
      setError(t('inventory.manualInventoryCheckSelectionRequired'));
      setManualProductId(null);
      return;
    }

    const existingDepositProduct = depositProducts.find(dp => dp.product.id === product.id);
    if (existingDepositProduct) {
      handleMoveRight(existingDepositProduct);
    } else {
      const extraProduct: DepositProduct = {
        id: Math.random(),
        product,
        quantity: -1,
        deposit: { id: selectedDeposit, name: '' }
      };
      setDepositProducts(previous => [...previous, extraProduct]);
      setMovedProducts(previous => {
        const existingIndex = previous.findIndex(item => item.product.id === product.id);
        if (existingIndex === -1) return [...previous, { product, count: 1 }];
        const updated = [...previous];
        updated[existingIndex] = { ...updated[existingIndex], count: updated[existingIndex].count + 1 };
        return updated;
      });
    }

    if (bulkScanMode) setBulkCount(previous => previous + 1);
    setSuccess(t('inventory.manualInventoryCheckCounted', { productName: getProductDisplayName(product) }));
    setManualProductId(null);
  };

  const handleManualUndo = (product: Product | undefined) => {
    setError('');
    setSuccess('');

    if (!product?.id) {
      setError(t('inventory.manualInventoryCheckSelectionRequired'));
      setManualProductId(null);
      return;
    }

    const countedProduct = movedProducts.find(item => item.product.id === product.id);
    if (!countedProduct) {
      setError(t('inventory.manualInventoryCheckNothingToUndo'));
      setManualProductId(null);
      return;
    }

    handleMoveLeft(countedProduct);
    if (bulkScanMode) setBulkCount(previous => Math.max(0, previous - 1));
    setSuccess(t('inventory.manualInventoryCheckUndone', { productName: getProductDisplayName(product) }));
    setManualProductId(null);
  };

  const fetchDepositProducts = async (depositId: number) => {
    try {
      console.log(`[InventoryPage] Fetching products for deposit ID: ${depositId}`);
      const response = await DepositApi.getDepositProducts(depositId);
      console.log(`[InventoryPage] Received ${response.data.length} products for deposit ${depositId}:`, response.data);
      setDepositProducts(response.data);
    } catch (err) {
      console.error(`[InventoryPage] Failed to load products for deposit ${depositId}:`, err);
      setError('Failed to load deposit products');
    }
  };

  const toggleBulkScanMode = () => {
    if (bulkScanMode) {
      // Exiting bulk scan mode
      setBulkScanMode(false);
      setBulkCount(0);
      setSuccess(`Bulk scan mode ended. Scanned ${bulkCount} products total.`);
      setError('');
    } else {
      // Entering bulk scan mode
      setBulkScanMode(true);
      setBulkCount(0);
      setSuccess('Bulk scan mode activated. Scan products to move them continuously.');
      setError('');
      // Ensure focus stays on input
      setTimeout(() => {
        if (scanInputRef.current) {
          scanInputRef.current.focus();
        }
      }, 200);
    }
  };

  // Conclude differences (for now, just alert summary)
  const handleConclude = () => {
    const missing = depositProducts
      .filter(p => p.quantity > 0)
      .map(p => `${p.product.name} - ${p.product.firm} - ${p.product.model} - ${p.product.size} (Expected: ${p.quantity})`);
    
    const extra = depositProducts
      .filter(p => p.quantity < 0)
      .map(p => `${p.product.name} - ${p.product.firm} - ${p.product.model} - ${p.product.size} (Found ${Math.abs(p.quantity)} extra items)`);
    
    const found = movedProducts.map(mp => `${mp.product.name} - ${mp.product.firm} - ${mp.product.model} - ${mp.product.size} (Scanned: ${mp.count})`);
    
    let summary = `INVENTORY ANALYSIS:\n\n`;
    summary += `MISSING FROM INVENTORY (${missing.length}):\n${missing.join('\n')}\n\n`;
    summary += `EXTRA PRODUCTS FOUND (${extra.length}):\n${extra.join('\n')}\n\n`;
    summary += `PRODUCTS SCANNED (${found.length}):\n${found.join('\n')}`;
    
    alert(summary);
  };

  // Actualize inventory - update database quantities to match scanned results
  const handleActualizeInventory = async () => {
    if (!selectedDeposit || !selectedCompany) {
      setError(t('inventory.selectDepositFirst'));
      return;
    }

    // Confirm action
    const confirmed = window.confirm(t('inventory.actualizeConfirm'));
    if (!confirmed) return;

    setError('');
    setSuccess('');

    try {
      // Prepare actualized product data - send as payload with depositId in params
      const actualizedProducts: Array<{productId: number, quantity: number}> = [];

      // Add scanned products with their actual counts
      for (const mp of movedProducts) {
        if (mp.product.id) { // Only include products with valid IDs
          actualizedProducts.push({
            productId: mp.product.id,
            quantity: mp.count
          });
        }
      }

      // Add remaining products from deposit with 0 quantity (they weren't scanned)
      for (const dp of depositProducts) {
        if (dp.quantity > 0 && dp.product.id) { // Only include products with valid IDs
          // Product was expected but not found - set to 0
          actualizedProducts.push({
            productId: dp.product.id,
            quantity: 0
          });
        }
        // Skip negative quantities (extra products) as they're already handled in scanned products
      }

      if (actualizedProducts.length === 0) {
        setError('No valid products to actualize');
        return;
      }

      console.log('Sending actualization request:', {
        depositId: selectedDeposit,
        payload: actualizedProducts
      });

      setSuccess(t('inventory.actualizeInProgress'));

      // Use the proper DepositApi method instead of direct axios call
      const response = await DepositApi.actualizeInventory(selectedDeposit, actualizedProducts);

      setSuccess(
        t('inventory.actualizeSuccess', { count: response.data.updatedProducts || actualizedProducts.length }) + 
        (response.data.auditTransactions ? ` (${response.data.auditTransactions} audit records created)` : '')
      );

      // Reset session state and refresh deposit products with updated quantities
      setMovedProducts([]);
      if (selectedDeposit) fetchDepositProducts(selectedDeposit);
      if (selectedCompany) fetchCompanyProducts(selectedCompany);

    } catch (err: any) {
      console.error('Failed to actualize inventory:', err);
      console.error('Error response:', err.response?.data);
      
      let errorMessage = t('inventory.actualizeError');
      if (err.response?.data?.message) {
        errorMessage += ': ' + err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage += ': ' + err.response.data.error;
      } else if (err.message) {
        errorMessage += ': ' + err.message;
      }
      
      setError(errorMessage);
    }
  };

  // Filter deposits to show only those associated with the selected company
  const filteredDeposits = selectedCompany 
    ? deposits.filter(deposit => {
        return deposit.company && deposit.company.id === selectedCompany;
      })
    : [];
  const manuallySelectedProduct = manualProductId == null
    ? undefined
    : companyProducts.find(product => product.id === manualProductId);
  const manuallySelectedCount = manuallySelectedProduct?.id == null
    ? 0
    : movedProducts.find(item => item.product.id === manuallySelectedProduct.id)?.count ?? 0;

  // Download Excel with statistics
  const handleDownloadExcel = () => {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
    
    const wsData = [
      ['Name', 'Firm', 'Model', 'Size', 'Expected in Deposit', 'Count Moved', 'Status'],
      ...[...depositProducts, ...movedProducts.filter(mp => !depositProducts.some(dp => dp.product.id === mp.product.id))].map(row => {
        const expectedQty = (row as any).quantity ?? 0;
        const movedQty = (movedProducts.find(mp => mp.product.id === row.product.id)?.count) ?? 0;
        let status = '';
        
        if (expectedQty < 0) {
          status = `EXTRA - Found ${Math.abs(expectedQty)} extra items not expected in deposit`;
        } else if (expectedQty > movedQty) {
          status = 'MISSING - Not all items scanned';
        } else if (expectedQty === movedQty) {
          status = 'COMPLETE - All items accounted for';
        } else {
          status = 'OVER - More items scanned than expected';
        }
        
        return [
          row.product.name,
          row.product.firm,
          row.product.model,
          row.product.size,
          expectedQty,
          movedQty,
          status
        ];
      })
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory_Analysis');
    XLSX.writeFile(wb, `inventory_analysis_${dateStr}.xlsx`);
  };

  function handleScan() {
    setError('');
    setSuccess('');
    
    if (!selectedDeposit) {
      setError('Please select a deposit first.');
      return;
    }
    
    if (!selectedCompany) {
      setError('Please select a company first.');
      return;
    }
    
    const value = scanValue.trim();
    if (!value) {
      setError('Please enter or scan a value.');
      return;
    }
    
    // Try to parse as Product ID (numeric or EAN-13 barcode)
    let productId: number | null = null;

    // If it's a JSON QR code (e.g. {"id":42,"firm":"Nike",...}), extract the id
    if (value.startsWith('{')) {
      try {
        const parsed = JSON.parse(value);
        if (parsed.id) productId = Number(parsed.id);
      } catch { /* not valid JSON, fall through */ }
    }

    // If it's a 13-digit EAN-13 code, extract the first 12 digits and parse as ID
    if (productId === null && /^\d{13}$/.test(value)) {
      productId = parseInt(value.substring(0, 12), 10);
    } 
    // If it's purely numeric, use directly as ID
    else if (productId === null && /^\d+$/.test(value)) {
      productId = parseInt(value, 10);
    }
    
    let found: DepositProduct | undefined;
    
    // Try to find by Product ID first in deposit products
    if (productId !== null) {
      found = depositProducts.find(dp => dp.product.id === productId);
    }
    
    // If not found by ID, try exact custom/derived barcode/QR match.
    if (!found) {
      found = depositProducts.find(dp => matchesScannedCode(dp.product, value));
    }

    // If still not found, try by firm-model-size (format: firm-model-size) for backward compatibility
    if (!found) {
      const parts = value.split('-').map(s => s.trim());
      if (parts.length === 3) {
        found = depositProducts.find(
          dp =>
            dp.product.firm?.toLowerCase() === parts[0].toLowerCase() &&
            dp.product.model?.toLowerCase() === parts[1].toLowerCase() &&
            dp.product.size?.toLowerCase() === parts[2].toLowerCase()
        );
      }
    }
    
    if (found) {
      // Product found in deposit - handle all quantities (positive, zero, or negative)
      // Always allow scanning - just decrease quantity by 1 and move to right table
      handleMoveRight(found);
      setScanValue('');
      
      if (bulkScanMode) {
        setBulkCount(prev => prev + 1);
        if (found.quantity <= 0) {
          setSuccess(`Bulk mode: Extra ${found.product.name} scanned (now ${found.quantity - 1}) (${bulkCount + 1} total)`);
        } else {
          setSuccess(`Bulk mode: Moved ${found.product.name} (${bulkCount + 1} total)`);
        }
        setTimeout(() => {
          if (scanInputRef.current) {
            scanInputRef.current.focus();
          }
        }, 100);
      } else {
        if (found.quantity <= 0) {
          setSuccess(`Extra ${found.product.name} scanned (now ${found.quantity - 1})`);
        } else {
          setSuccess(`Moved ${found.product.name} to scanned products`);
        }
        scanInputRef.current?.focus();
      }
      return;
    }
    
    // Product not found in deposit - search in company products by ID
    let product: Product | undefined;
    
    if (productId !== null) {
      product = companyProducts.find((p: Product) => p.id === productId);
    }
    
    // If not found by ID, try exact custom/derived barcode/QR match.
    if (!product) {
      product = companyProducts.find((p: Product) => matchesScannedCode(p, value));
    }

    // If still not found, try by firm-model-size for backward compatibility
    if (!product) {
      const parts = value.split('-').map(s => s.trim());
      if (parts.length === 3) {
        product = companyProducts.find((p: Product) =>
          p.firm?.toLowerCase() === parts[0].toLowerCase() &&
          p.model?.toLowerCase() === parts[1].toLowerCase() &&
          p.size?.toLowerCase() === parts[2].toLowerCase()
        );
      }
    }
    
    if (product) {
      // Product exists in company but not in deposit - add it with 0 quantity, then decrease to -1
      const newDepositProduct: DepositProduct = {
        id: Math.random(), // temporary ID
        product: product,
        quantity: -1, // Start with -1 since it's an extra product not expected in deposit
        deposit: { id: selectedDeposit!, name: '' }
      };
      
      setDepositProducts(prev => [...prev, newDepositProduct]);
      
      // Also add to moved products
      setMovedProducts(prev => {
        const idx = prev.findIndex(mp => mp.product.id === product!.id);
        if (idx !== -1) {
          const copy = [...prev];
          copy[idx] = { ...copy[idx], count: copy[idx].count + 1 };
          return copy;
        } else {
          return [...prev, { product: product!, count: 1 }];
        }
      });
      
      if (bulkScanMode) {
        setBulkCount(prev => prev + 1);
        setSuccess(`Bulk mode: Added extra product ${product.name} (${bulkCount + 1} total)`);
        setTimeout(() => {
          if (scanInputRef.current) {
            scanInputRef.current.focus();
          }
        }, 100);
      } else {
        setSuccess(`Added extra product ${product.name} to deposit with -1 quantity`);
        scanInputRef.current?.focus();
      }
    } else {
      setError('Product not found in selected company.');
    }
    
    setScanValue('');
    
    if (bulkScanMode && scanInputRef.current) {
      setTimeout(() => {
        scanInputRef.current?.focus();
      }, 100);
    } else if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px #0001', padding: 32 }}>
      <h2 style={{ color: '#1976d2', marginBottom: 24 }}>{t('inventory.inventoryCheck')}</h2>
      <div style={{ marginBottom: 18 }}>
        <label style={{ fontWeight: 600, color: '#1976d2', marginRight: 12 }}>{t('inventory.selectCompany')}:</label>
        <select
          value={selectedCompany ?? ''}
          onChange={e => setSelectedCompany(e.target.value ? Number(e.target.value) : null)}
          style={{ padding: 8, borderRadius: 6, border: '1px solid #1976d2', minWidth: 180 }}
        >
          <option value="">-- {t('inventory.chooseCompany')} --</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>
      
      <div style={{ marginBottom: 18 }}>
        <label style={{ fontWeight: 600, color: '#1976d2', marginRight: 12 }}>{t('inventory.selectDeposit')}:</label>
        <select
          value={selectedDeposit ?? ''}
          onChange={e => setSelectedDeposit(e.target.value ? Number(e.target.value) : null)}
          style={{ 
            padding: 8, 
            borderRadius: 6, 
            border: '1px solid #1976d2', 
            minWidth: 180,
            opacity: !selectedCompany ? 0.6 : 1
          }}
          disabled={!selectedCompany}
        >
          <option value="">
            {!selectedCompany ? `-- ${t('inventory.selectCompanyFirst')} --` : `-- ${t('inventory.chooseDeposit')} --`}
          </option>
          {selectedCompany && filteredDeposits.map(d => (
            <option key={d.id} value={d.id}>
              {d.name}
              {d.company ? ` (${d.company.name})` : ''}
            </option>
          ))}
        </select>
        {!selectedCompany && (
          <div style={{ fontSize: 12, color: '#f44336', marginTop: 4 }}>
            {t('inventory.selectCompanyFirst')}
          </div>
        )}
        {selectedCompany && filteredDeposits.length === 0 && (
          <div style={{ fontSize: 12, color: '#f44336', marginTop: 4 }}>
            {t('inventory.noDepositsFound')}
          </div>
        )}
      </div>
      <button onClick={handleConclude} style={{ marginBottom: 18, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 24px', fontWeight: 600 }}>{t('inventory.concludeDifferences')}</button>
      <button onClick={handleDownloadExcel} style={{ marginLeft: 16, marginBottom: 18, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 24px', fontWeight: 600 }}>{t('inventory.downloadExcel')}</button>
      <button 
        onClick={handleActualizeInventory} 
        style={{ 
          marginLeft: 16, 
          marginBottom: 18, 
          background: '#ff6f00', 
          color: '#fff', 
          border: 'none', 
          borderRadius: 6, 
          padding: '8px 24px', 
          fontWeight: 600,
          cursor: selectedDeposit && selectedCompany ? 'pointer' : 'not-allowed',
          opacity: selectedDeposit && selectedCompany ? 1 : 0.6
        }}
        disabled={!selectedDeposit || !selectedCompany}
        title={t('inventory.actualizeInventoryDescription')}
      >
        📊 {t('inventory.actualizeInventory')}
      </button>
      
      {/* Bulk Mode Controls */}
      {selectedDeposit && selectedCompany && (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 16, 
          marginBottom: 16,
          padding: 16,
          background: bulkScanMode ? '#e8f5e8' : '#f8f9fa',
          borderRadius: 8,
          border: bulkScanMode ? '2px solid #4caf50' : '1px solid #e0e0e0'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>
              Scanning Mode:
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              {bulkScanMode ? `Bulk Scan Mode Active (${bulkCount} moved)` :
               'Normal Mode - Click input field for each scan'}
            </div>
          </div>
          
          <button
            onClick={toggleBulkScanMode}
            style={{
              padding: '10px 20px',
              borderRadius: 6,
              border: 'none',
              background: bulkScanMode ? '#4caf50' : '#e0e0e0',
              color: bulkScanMode ? 'white' : '#333',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontSize: 14
            }}
          >
            {bulkScanMode ? 'Exit Bulk Scan' : 'Bulk Scan Mode'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18, background: '#f5f8fa', borderRadius: 8, padding: 12, boxShadow: '0 1px 4px #0001' }}>
        <input
          type="text"
          ref={scanInputRef}
          autoFocus
          placeholder={
            bulkScanMode ? "Bulk Scan Mode: Scan products to move them continuously" :
            "Scan or enter barcode/QR or text (firm-model-size)"
          }
          value={scanValue}
          onChange={e => {
            setScanValue(e.target.value);
            // Clear messages when user starts typing
            if (e.target.value && !bulkScanMode) {
              setError('');
              setSuccess('');
            }
          }}
          onKeyDown={e => { if (e.key === 'Enter') handleScan(); }}
          style={{ 
            padding: 8, 
            borderRadius: 4, 
            border: bulkScanMode ? '2px solid #4caf50' : '1px solid #1976d2', 
            width: 260,
            background: bulkScanMode ? '#f0fff0' : 'white'
          }}
          disabled={selectedDeposit == null || selectedCompany == null}
        />
        <button onClick={handleScan} title="Scan Product" style={{ background: 'none', border: 'none', borderRadius: '50%', padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} disabled={selectedDeposit == null || selectedCompany == null}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="#1976d2" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="12" fill="#e3f2fd"/>
            <path d="M12 7v10M7 12h10" stroke="#1976d2" strokeWidth="2.2" strokeLinecap="round"/>
          </svg>
        </button>
        <details
          open={manualEntryOpen}
          onToggle={event => setManualEntryOpen(event.currentTarget.open)}
          style={{ flexBasis: '100%', borderTop: '1px solid #dbe5ef', paddingTop: 10 }}
        >
          <summary style={{ cursor: 'pointer', color: '#475569', fontSize: 14, fontWeight: 600 }}>
            {bulkScanMode
              ? t('inventory.manualInventoryCheckBulkToggle')
              : t('inventory.manualInventoryCheckToggle')}
          </summary>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingTop: 12 }}>
            <select
              aria-label={t('inventory.manualInventoryCheckProduct')}
              value={manualProductId ?? ''}
              onChange={event => {
                setManualProductId(event.target.value ? Number(event.target.value) : null);
                setError('');
                setSuccess('');
              }}
              disabled={!selectedDeposit || !selectedCompany}
              style={{
                padding: 10, borderRadius: 6, border: '1px solid #94a3b8',
                minWidth: 300, maxWidth: '100%', background: 'white', fontSize: 14
              }}
            >
              <option value="">{t('inventory.manualInventoryCheckProduct')}</option>
              {companyProducts.filter(product => product.id != null).map(product => (
                <option key={product.id} value={product.id}>{getProductDisplayName(product)}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => handleManualCount(manuallySelectedProduct)}
              disabled={!manuallySelectedProduct}
              style={{
                padding: '10px 16px', border: 0, borderRadius: 6, color: 'white',
                background: '#4caf50', fontWeight: 700,
                cursor: manuallySelectedProduct ? 'pointer' : 'not-allowed',
                opacity: manuallySelectedProduct ? 1 : 0.55
              }}
            >
              +1 {t('inventory.manualInventoryCheckCountOne')}
            </button>
            <button
              type="button"
              onClick={() => handleManualUndo(manuallySelectedProduct)}
              disabled={!manuallySelectedProduct || manuallySelectedCount === 0}
              style={{
                padding: '10px 16px', border: 0, borderRadius: 6, color: 'white',
                background: '#475569', fontWeight: 700,
                cursor: manuallySelectedProduct && manuallySelectedCount > 0 ? 'pointer' : 'not-allowed',
                opacity: manuallySelectedProduct && manuallySelectedCount > 0 ? 1 : 0.55
              }}
            >
              −1 {t('inventory.manualInventoryCheckUndoOne')}
            </button>
            {manuallySelectedCount > 0 && (
              <span style={{ color: '#475569', fontSize: 13, fontWeight: 600 }}>
                {t('inventory.manualInventoryCheckCurrentCount', { count: manuallySelectedCount })}
              </span>
            )}
            <div style={{ flexBasis: '100%', color: '#64748b', fontSize: 13 }}>
              {t('inventory.manualInventoryCheckHelp')}
            </div>
          </div>
        </details>
        {error && <span style={{ 
          color: '#f44336', 
          marginLeft: 8,
          fontWeight: 'normal'
        }}>{error}</span>}
        {success && <span style={{ 
          color: '#4caf50', 
          marginLeft: 8,
          fontWeight: 600
        }}>{success}</span>}
      </div>
      
      {/* Legend for negative quantities */}
      <div style={{ 
        marginBottom: 16, 
        padding: 12, 
        background: '#fff3cd', 
        border: '1px solid #ffeaa7', 
        borderRadius: 6,
        fontSize: 14
      }}>
        <strong>Scanning Behavior:</strong> 
        <span style={{ marginLeft: 8, color: '#4caf50' }}>Positive quantities</span> = Decrease by 1 each scan (remove when 0) | 
        <span style={{ marginLeft: 8, color: '#f44336', fontWeight: 600 }}>Negative quantities (Extra)</span> = Continue decreasing (-1, -2, -3, etc.)
      </div>
      
      <div style={{ display: 'flex', gap: 32 }}>
        {/* Inventory Table (Left) */}
        <div style={{ flex: 1 }}>
          <h3 style={{ color: '#1976d2' }}>Products in Inventory</h3>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: '#f5f8fa', borderRadius: 8, boxShadow: '0 1px 4px #0001' }}>
            <thead>
              <tr style={{ background: '#1976d2', color: '#fff' }}>
                <th style={{ padding: '10px 8px' }}>Name</th>
                <th style={{ padding: '10px 8px' }}>Firm</th>
                <th style={{ padding: '10px 8px' }}>Model</th>
                <th style={{ padding: '10px 8px' }}>Size</th>
                <th style={{ padding: '10px 8px' }}>Count</th>
                <th style={{ padding: '10px 8px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {depositProducts.map((item, idx) => {
                const prod = item.product;
                return (
                  <tr key={prod.id || idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f0f4fa' }}>
                    <td style={{ padding: '8px 6px', textAlign: 'center' }}>{prod.name || '-'}</td>
                    <td style={{ padding: '8px 6px', textAlign: 'center' }}>{prod.firm || '-'}</td>
                    <td style={{ padding: '8px 6px', textAlign: 'center' }}>{prod.model || '-'}</td>
                    <td style={{ padding: '8px 6px', textAlign: 'center' }}>{prod.size || '-'}</td>
                    <td style={{ 
                      padding: '8px 6px', 
                      textAlign: 'center',
                      color: item.quantity < 0 ? '#f44336' : 'inherit',
                      fontWeight: item.quantity < 0 ? 'bold' : 'normal',
                      background: item.quantity < 0 ? '#ffebee' : 'transparent'
                    }}>
                      {item.quantity ?? 1} {item.quantity < 0 ? `(Extra x${Math.abs(item.quantity)})` : ''}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                      {item.quantity > 0 ? (
                        <button 
                          onClick={() => handleMoveRight(item)} 
                          style={{ 
                            background: '#4caf50', 
                            color: '#fff', 
                            border: 'none', 
                            borderRadius: 4, 
                            padding: '4px 12px', 
                            fontWeight: 600, 
                            cursor: 'pointer' 
                          }}
                          title="Scan/Move to scanned list (decreases by 1)"
                        >
                          -
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleMoveRight(item)} 
                          style={{ 
                            background: '#f44336', 
                            color: '#fff', 
                            border: 'none', 
                            borderRadius: 4, 
                            padding: '4px 8px', 
                            fontWeight: 600, 
                            cursor: 'pointer',
                            fontSize: 12
                          }}
                          title="Scan extra product again (makes it more negative)"
                        >
                          -1
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Moved Table (Right) */}
        <div style={{ flex: 1 }}>
          <h3 style={{ color: '#1976d2' }}>Moved Products</h3>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, background: '#f5f8fa', borderRadius: 8, boxShadow: '0 1px 4px #0001' }}>
            <thead>
              <tr style={{ background: '#1976d2', color: '#fff' }}>
                <th style={{ padding: '10px 8px' }}>Name</th>
                <th style={{ padding: '10px 8px' }}>Firm</th>
                <th style={{ padding: '10px 8px' }}>Model</th>
                <th style={{ padding: '10px 8px' }}>Size</th>
                <th style={{ padding: '10px 8px' }}>Count</th>
                <th style={{ padding: '10px 8px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {movedProducts.map((mp, idx) => (
                <tr key={mp.product.id || idx} style={{ background: idx % 2 === 0 ? '#fff' : '#e3f2fd' }}>
                  <td style={{ padding: '8px 6px', textAlign: 'center' }}>{mp.product.name || '-'}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'center' }}>{mp.product.firm || '-'}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'center' }}>{mp.product.model || '-'}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'center' }}>{mp.product.size || '-'}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'center' }}>{mp.count}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                    <button onClick={() => handleMoveLeft(mp)} style={{ background: '#000', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 12px', fontWeight: 600, cursor: 'pointer' }}>-</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

